import type { FC, SetStateAction } from "react";
import { useState } from "react";
import type { ValidationError } from "@tanstack/react-form";
import { useForm } from "@tanstack/react-form";
import { isAxiosError } from "axios";
import { useNavigate } from "react-router-dom";

import { useCreatePatient } from "@/api/patients";
import AddUser from "@/assets/icons/add-user";
import { FieldInput, FieldRadio, FieldSelect } from "@/components/form";
import FieldDatepicker from "@/components/form/field-datepicker";
import DialogCustom from "@/components/modals/dialog-custom";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { emailSchema } from "@/schemas/auth";
import {
  createPatientDefaultValues,
  createPatientSchema,
} from "@/schemas/patient";
import { resetForm, stopPropagation } from "@/utils/form";
import { consultationOptions, genderOptions } from "@/utils/patient";

interface ModalProps {
  open: boolean;
  setOpen: React.Dispatch<SetStateAction<boolean>>;
}

const AddPatientModal: FC<ModalProps> = ({ open, setOpen }) => {
  const { mutate } = useCreatePatient();
  const navigate = useNavigate();
  const [backToTable, setBackToTable] = useState(true);

  const { Field, handleSubmit } = useForm({
    defaultValues: createPatientDefaultValues,
    validators: {
      onSubmit: ({ value }) => {
        const { email, birthDate, lastName, firstName } = value;
        const returnValue: { fields: { [k: string]: ValidationError } } = {
          fields: {},
        };
        if (!emailSchema.safeParse(email).success) {
          const emailError: ValidationError = "Veuillez entrer un email valide";
          returnValue.fields["email"] = emailError;
        }
        if (!birthDate) {
          const birthDateError: ValidationError =
            "Veuillez entrer une date de naissance";
          returnValue.fields["birthDate"] = birthDateError;
        }
        if (!lastName) {
          const lastNameError: ValidationError =
            "Veuillez entrer un nom de famille";
          returnValue.fields["lastName"] = lastNameError;
        }
        if (!firstName) {
          const firstNameError: ValidationError = "Veuillez entrer un prénom";
          returnValue.fields["firstName"] = firstNameError;
        }
        return returnValue;
      },
    },
    onSubmit: async ({ value, formApi }) => {
      const values = createPatientSchema.parse(value);
      mutate(value, {
        onSuccess: (data) => {
          if (backToTable) {
            setOpen(false);
            resetForm(values, formApi);
          } else {
            navigate("/patients/fiche", { state: { patientId: data.id } });
          }
        },
        onError: (e) => {
          if (isAxiosError(e)) {
            if (e.code === "ERR_NETWORK")
              return {
                fields: {
                  lastName: "Erreur réseau, merci de réessayer ultérieurement",
                },
              };
          }
        },
      });
    },
  });

  return (
    <DialogCustom
      title="Ajouter un patient"
      startIcon={
        <div className="flex p-3 w-12 h-12 justify-center items-center border border-gray-200 rounded-[10px]">
          <AddUser className="w-6 h-6" color="black" />
        </div>
      }
      open={open}
      onOpenChange={setOpen}
      className="flex flex-col gap-4"
    >
      <Separator className="col-span-full bg-gray-200" />
      <form
        onSubmit={(e) => stopPropagation(e, handleSubmit)}
        className="grid grid-flow-row grid-cols-1 gap-6 px-6 w-full"
      >
        <FieldSelect
          label="Motif de la consultation"
          required
          schema={createPatientSchema}
          Field={Field}
          name="consultationMotive"
          options={consultationOptions}
          validators={{
            onChange: createPatientSchema.shape.consultationMotive,
          }}
        />
        <FieldRadio
          label="Sexe"
          required
          schema={createPatientSchema}
          Field={Field}
          name="gender"
          options={genderOptions}
          orientation="horizontal"
          validators={{
            onChange: createPatientSchema.shape.gender,
          }}
        />
        <FieldInput
          label="Nom du patient"
          placeholder="Nom"
          required
          schema={createPatientSchema}
          Field={Field}
          name="lastName"
          validators={{
            onChange: createPatientSchema.shape.lastName,
          }}
        />
        <FieldInput
          label="Prénom du patient"
          placeholder="Prénom"
          required
          schema={createPatientSchema}
          Field={Field}
          name="firstName"
          validators={{
            onChange: createPatientSchema.shape.firstName,
          }}
        />
        <FieldDatepicker
          label="Date de naissance"
          required
          schema={createPatientSchema}
          Field={Field}
          name="birthDate"
          disabled={false}
          validators={{
            onChange: createPatientSchema.shape.birthDate,
          }}
        />
        <FieldInput
          label="Email"
          placeholder="Email"
          required
          schema={createPatientSchema}
          Field={Field}
          name="email"
          validators={{
            onChange: createPatientSchema.shape.email,
          }}
        />
        <input type="submit" className="hidden" />
      </form>
      <Separator className="col-span-full bg-gray-200" />
      <DialogFooter className="flex flex-col gap-3 px-6">
        <Button
          type="button"
          onClick={() => {
            setBackToTable(false);
            handleSubmit();
          }}
          variant={"outline"}
        >
          Remplir la fiche patient
        </Button>
        <Button
          type="button"
          variant={"blue"}
          onClick={() => {
            handleSubmit();
          }}
        >
          Aller au dashboard patient
        </Button>
      </DialogFooter>
    </DialogCustom>
  );
};
export default AddPatientModal;
